/* USC hero energy: direct canvas motion is scoped only to the existing homepage logo wrapper. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function mountHeroEnergy(wrapper) {
    if (!wrapper || wrapper.dataset.uscEnergyMounted || reducedMotion.matches) return;
    const logo = wrapper.querySelector('.usc-hero__motion-logo');
    if (!logo) return;

    wrapper.dataset.uscEnergyMounted = 'true';
    logo.style.setProperty('animation', 'none', 'important');
    logo.style.setProperty('will-change', 'transform, filter');
    wrapper.querySelectorAll('.usc-hero__energy-trail, .usc-hero__energy-dot').forEach((element) => {
      element.style.setProperty('display', 'none', 'important');
    });

    const canvas = document.createElement('canvas');
    canvas.className = 'usc-hero__energy-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    Object.assign(canvas.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '5',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      mixBlendMode: 'screen'
    });
    wrapper.appendChild(canvas);

    const context = canvas.getContext('2d');
    const mobile = window.matchMedia('(max-width: 560px)').matches;
    const particleCount = mobile ? 7 : 14;
    const particles = Array.from({ length: particleCount }, (_, index) => ({
      angle: ((Math.PI * 2) / particleCount) * index + 0.16,
      radius: 0.32 + ((index * 19) % 13) / 100,
      speed: 0.035 + ((index * 7) % 7) / 100,
      phase: index * 0.63,
      size: index % 4 === 0 ? 2 : 1.25
    }));

    let width = 1;
    let height = 1;
    let pixelRatio = 1;

    function resize() {
      const bounds = wrapper.getBoundingClientRect();
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    }

    new ResizeObserver(resize).observe(wrapper);
    resize();

    function draw(timeInMs) {
      const time = timeInMs / 1000;
      const cx = width / 2;
      const cy = height / 2;
      const basePulse = 0.5 + 0.5 * Math.sin(time * 0.88);
      const surge = Math.pow(0.5 + 0.5 * Math.sin(time * 0.37 - 1.15), 11);
      const fieldStrength = 0.26 + basePulse * 0.11 + surge * 0.38;

      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = 'lighter';

      const field = context.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height) * 0.56);
      field.addColorStop(0, `rgba(240,238,229,${0.03 + fieldStrength * 0.17})`);
      field.addColorStop(0.28, `rgba(183,84,56,${0.02 + fieldStrength * 0.13})`);
      field.addColorStop(0.57, `rgba(183,84,56,${fieldStrength * 0.035})`);
      field.addColorStop(1, 'rgba(0,0,0,0)');
      context.fillStyle = field;
      context.fillRect(0, 0, width, height);

      context.lineCap = 'round';
      const orbitAlpha = 0.14 + fieldStrength * 0.45;
      context.strokeStyle = `rgba(240,238,229,${orbitAlpha})`;
      context.lineWidth = mobile ? 0.85 : 1.25;
      context.beginPath();
      context.ellipse(cx, cy, width * 0.435, height * 0.355, -0.09, time * 0.31, time * 0.31 + Math.PI * 0.42);
      context.stroke();
      context.strokeStyle = `rgba(183,84,56,${0.10 + fieldStrength * 0.39})`;
      context.beginPath();
      context.ellipse(cx, cy, width * 0.37, height * 0.43, 0.11, Math.PI - time * 0.27, Math.PI * 1.36 - time * 0.27);
      context.stroke();

      particles.forEach((particle) => {
        const angle = particle.angle + time * particle.speed * Math.PI * 2;
        const x = cx + Math.cos(angle) * width * particle.radius;
        const y = cy + Math.sin(angle) * height * (particle.radius * 0.64);
        const shimmer = 0.57 + 0.43 * Math.sin(time * 1.7 + particle.phase);
        const alpha = (0.18 + fieldStrength * 0.8) * shimmer;
        const tailLength = (mobile ? 9 : 19) + surge * (mobile ? 8 : 18);
        const tailX = x - Math.cos(angle) * tailLength;
        const tailY = y - Math.sin(angle) * tailLength;

        const trail = context.createLinearGradient(tailX, tailY, x, y);
        trail.addColorStop(0, 'rgba(240,238,229,0)');
        trail.addColorStop(0.6, `rgba(183,84,56,${alpha * 0.45})`);
        trail.addColorStop(1, `rgba(240,238,229,${alpha})`);
        context.strokeStyle = trail;
        context.lineWidth = mobile ? 0.9 : 1.35;
        context.beginPath();
        context.moveTo(tailX, tailY);
        context.lineTo(x, y);
        context.stroke();

        const glow = context.createRadialGradient(x, y, 0, x, y, particle.size * (5 + surge * 3));
        glow.addColorStop(0, `rgba(240,238,229,${alpha})`);
        glow.addColorStop(0.2, `rgba(183,84,56,${alpha * 0.46})`);
        glow.addColorStop(1, 'rgba(0,0,0,0)');
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, particle.size * (5 + surge * 3), 0, Math.PI * 2);
        context.fill();
      });

      context.globalCompositeOperation = 'source-over';
      const logoScale = 1 + basePulse * 0.004 + surge * 0.017;
      logo.style.transform = `scale(${logoScale})`;
      logo.style.filter = `brightness(${1.04 + surge * 0.34}) contrast(${1.03 + surge * 0.12})`;
      window.requestAnimationFrame(draw);
    }

    window.requestAnimationFrame(draw);
  }

  function init() {
    document.querySelectorAll('.usc-hero__motion-wrap').forEach(mountHeroEnergy);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
